// Phase 308 — Single source of truth for the Add Expense + Edit Expense
// form UI. Identical field order, spacing, labels, dropdowns, widths, and
// validation between both flows per user spec.
//
// Props
//   form, setForm            — controlled form state object
//   mode                     — 'add' | 'edit' (only used for data-testid)
//   t                        — i18n function from useLanguage
//   getCatLabel              — category code → label helper
//   customCategories         — list of {category_code, group}
//   vendorList               — list of {vendor_id, name}
//   building                 — current building doc (insurance fields, etc.)
//   paidInstallments         — installments already paid for insurance
//   sourceBalances           — {cash, bank, reserve_fund} availability
//   onCreateNewVendor        — () => void, opens the quick vendor dialog
//   onFetchPaidInstallments  — (insuranceId) => void
//   attachments              — list of saved attachments (server side)
//   pendingFiles             — list of File[] queued before save (for Add)
//   onPickFiles              — (files: FileList) => void
//   onRemovePending          — (index: number) => void
//   onRemoveAttachment       — (attachmentId: string) => void
//   onDownloadAttachment     — (attachmentId: string) => void

import React, { useRef, useEffect, useState } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectGroup, SelectLabel } from './ui/select';
import { Button } from './ui/button';
import DatePickerDMY from './DatePickerDMY';
import { Paperclip, X, DownloadSimple, FilePdf, FileImage, FileXls, FileDoc, File as FileIcon } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;
// Phase 708 — Cache the Reserve Fund reasons list locally so the dropdown
// doesn't re-fetch on every render. Resolved on first mount.
let _rfReasonsCache = null;

const ALLOWED_EXT = ['pdf','jpg','jpeg','png','heic','heif','webp','gif','xls','xlsx','csv','doc','docx','txt'];
const ACCEPT = '.pdf,.jpg,.jpeg,.png,.heic,.heif,.webp,.gif,.xls,.xlsx,.csv,.doc,.docx,.txt,application/pdf,image/*';

const formatBytes = (b) => {
  if (!b && b !== 0) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
};

const extOf = (name) => (name || '').toLowerCase().split('.').pop();

const IconForFile = ({ name }) => {
  const e = extOf(name);
  if (e === 'pdf') return <FilePdf size={16} className="text-red-500" />;
  if (['jpg','jpeg','png','heic','heif','webp','gif'].includes(e)) return <FileImage size={16} className="text-blue-500" />;
  if (['xls','xlsx','csv'].includes(e)) return <FileXls size={16} className="text-emerald-600" />;
  if (['doc','docx'].includes(e)) return <FileDoc size={16} className="text-indigo-600" />;
  return <FileIcon size={16} className="text-zinc-500" />;
};

export default function ExpenseFormFields({
  form,
  setForm,
  mode = 'add',
  t,
  getCatLabel,
  customCategories = [],
  vendorList = [],
  building = null,
  paidInstallments = [],
  sourceBalances = {},
  onCreateNewVendor,
  onFetchPaidInstallments,
  attachments = [],
  pendingFiles = [],
  onPickFiles,
  onRemovePending,
  onRemoveAttachment,
  onDownloadAttachment,
}) {
  const tid = (suffix) => `${mode === 'edit' ? 'edit' : 'add'}-expense-${suffix}`;
  const fileInputRef = useRef(null);

  // Phase 708 — Reserve Fund reasons (loaded once).
  const [rfReasons, setRfReasons] = useState(_rfReasonsCache || []);
  useEffect(() => {
    if (_rfReasonsCache) return;
    axios.get(`${API}/api/reserve-fund/reasons`)
      .then(r => {
        _rfReasonsCache = r.data?.reasons || [];
        setRfReasons(_rfReasonsCache);
      })
      .catch(() => { /* dropdown will show empty; backend still validates */ });
  }, []);

  const monthlyCat = (customCategories || []).find(c => c.category_code === form.category && c.group === 'monthly');
  const isMonthlyRecurring = !!monthlyCat;

  const isInsurance = form.category === '306' && building?.insurance_id;
  const isFullInsurance = isInsurance && form.pay_full;

  return (
    <div className="space-y-4">
      {/* 1) Category */}
      <div>
        <Label>{t('expenseCategory')}</Label>
        <Select value={form.category} onValueChange={(val) => {
          const isIns = val === '306' && building?.insurance_id;
          const becomesMonthly = (customCategories || []).some(c => c.category_code === val && c.group === 'monthly');
          const updated = { ...form, category: val, pay_full: false, installment_number: '', insurance_id: '' };
          if (isIns) {
            updated.amount = building.insurance_installment_amount || 0;
            onFetchPaidInstallments && onFetchPaidInstallments(building.insurance_id);
          }
          if (becomesMonthly && !updated.billing_period_start) {
            const now = new Date();
            const y = now.getFullYear();
            const m = String(now.getMonth() + 1).padStart(2, '0');
            const last = new Date(y, now.getMonth() + 1, 0).getDate();
            updated.billing_period_start = `${y}-${m}-01`;
            updated.billing_period_end = `${y}-${m}-${String(last).padStart(2, '0')}`;
          }
          if (!becomesMonthly && !isIns) {
            updated.billing_period_start = '';
            updated.billing_period_end = '';
          }
          setForm(updated);
        }}>
          <SelectTrigger className="mt-1 rounded-none" data-testid={tid('category')}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectGroup><SelectLabel>{t('monthlyExpenses')}</SelectLabel>{customCategories.filter(c => c.group === 'monthly').map(c => <SelectItem key={c.category_code} value={c.category_code}>{getCatLabel(c.category_code, t, customCategories)}</SelectItem>)}</SelectGroup>
            <SelectGroup><SelectLabel>{t('capitalExpenses')}</SelectLabel>{customCategories.filter(c => c.group === 'capital').map(c => <SelectItem key={c.category_code} value={c.category_code}>{getCatLabel(c.category_code, t, customCategories)}</SelectItem>)}</SelectGroup>
          </SelectContent>
        </Select>
      </div>

      {/* 2) Reference / Receipt Number */}
      <div>
        <Label>{t('referenceNumber')}</Label>
        <Input
          value={form.reference_number || ''}
          onChange={(e) => setForm({ ...form, reference_number: e.target.value })}
          className="mt-1 rounded-none font-mono"
          placeholder={t('referenceNumberHint')}
          data-testid={tid('reference')}
        />
      </div>

      {/* 3) Pay Full toggle — insurance only */}
      {isInsurance && (
        <label className="flex items-center gap-2 cursor-pointer p-3 border border-emerald-200 bg-emerald-50" data-testid={tid('insurance-pay-full-toggle')}>
          <input type="checkbox" checked={!!form.pay_full} onChange={(e) => setForm({ ...form, pay_full: e.target.checked, installment_number: '' })} className="w-4 h-4 accent-emerald-600" />
          <span className="text-sm font-semibold text-emerald-800">{t('payFullAmount')}</span>
          <span className="text-xs font-mono text-emerald-600 ml-auto">&euro;{(building?.insurance_total_premium || 0).toFixed(2)}</span>
        </label>
      )}

      {/* 4) Amount + Date */}
      <div className="grid grid-cols-2 gap-4">
        {!isFullInsurance && (
          <div>
            <Label>{t('amount')} (&euro;)</Label>
            <Input type="number" step="0.01" min="0" value={form.amount ?? ''} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} className="mt-1 rounded-none font-mono" required data-testid={tid('amount')} />
          </div>
        )}
        <div className={isFullInsurance ? 'col-span-2' : ''}>
          <Label>{t('expenseDate')}</Label>
          <DatePickerDMY value={form.date || ''} onChange={v => setForm({ ...form, date: v })} className="mt-1 font-mono" required data-testid={tid('date')} />
        </div>
      </div>

      {/* 5) Billing Period — From/To dates for ALL monthly recurring
          categories. Phase 735.27: previously only Electricity (204) used
          From/To; other utilities (Water, Common Electricity, custom
          monthly categories, etc.) used Month + Year dropdowns which
          could not represent multi-month billing periods (e.g. 01/02/2025
          – 28/02/2025 or longer). Consolidated so every utility that can
          span multiple months uses the same UI. Insurance (306) is
          excluded — it has its own installment picker. */}
      {isMonthlyRecurring && form.category !== '306' && (
        <div className="grid grid-cols-2 gap-4" data-testid={tid('billing-period')}>
          <div>
            <Label className="text-xs text-zinc-500">{t('billingPeriodFrom') || t('dateFrom') || 'Billing Period From'}</Label>
            <DatePickerDMY value={form.billing_period_start || ''} onChange={v => setForm({ ...form, billing_period_start: v })} className="mt-1 font-mono" data-testid={tid('billing-from-date')} />
          </div>
          <div>
            <Label className="text-xs text-zinc-500">{t('billingPeriodTo') || t('dateTo') || 'Billing Period To'}</Label>
            <DatePickerDMY value={form.billing_period_end || ''} onChange={v => setForm({ ...form, billing_period_end: v })} className="mt-1 font-mono" data-testid={tid('billing-to-date')} />
          </div>
        </div>
      )}

      {/* 6) Vendor / Insurance company */}
      {isInsurance ? (
        <>
          <div className="p-3 bg-blue-50 border border-blue-200" data-testid={tid('insurance-company-info')}>
            <p className="text-xs text-blue-500 uppercase font-semibold">{t('insuranceCompany')}</p>
            <p className="text-sm font-bold text-blue-900 mt-1">{building?.insurance_company || '\u2014'}</p>
            <p className="text-xs text-blue-600 font-mono mt-0.5">{t('policyNumber')}: {building?.insurance_policy_number || '\u2014'}</p>
          </div>
          {!form.pay_full && (
            <div>
              <Label>{t('selectInstallment')}</Label>
              <Select value={form.installment_number ? String(form.installment_number) : ''} onValueChange={(val) => setForm({ ...form, installment_number: val })}>
                <SelectTrigger className="mt-1 rounded-none" data-testid={tid('insurance-installment-select')}><SelectValue placeholder={t('selectInstallment')} /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: building?.insurance_num_installments || 1 }, (_, i) => i + 1).map(num => {
                    const taken = paidInstallments.find(p => p.installment_number === num);
                    const isPaid = taken && taken.status === 'paid';
                    return (
                      <SelectItem key={num} value={String(num)} disabled={isPaid}>
                        {t('installment')} {num} / {building?.insurance_num_installments || 1}
                        {isPaid ? ` \u2014 ${t('paid')}` : taken ? ` \u2014 ${t('partiallyPaid')}` : ''}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          )}
        </>
      ) : (
        <div>
          <Label>{t('selectVendor')}</Label>
          <Select value={form.vendor_id || '_none'} onValueChange={(val) => {
            if (val === '_create_new') { onCreateNewVendor && onCreateNewVendor(); }
            else { setForm({ ...form, vendor_id: val === '_none' ? '' : val }); }
          }}>
            <SelectTrigger className="mt-1 rounded-none" data-testid={tid('vendor')}><SelectValue placeholder={t('selectVendor')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">— {t('noSelection') || 'None'} —</SelectItem>
              {vendorList.map(v => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.name}</SelectItem>)}
              <SelectItem value="_create_new" className="text-blue-600 font-semibold">+ {t('createNewVendor')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* 7) Notes */}
      <div>
        <Label>{t('notes')}</Label>
        <Textarea value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-none" rows={2} data-testid={tid('notes')} />
      </div>

      {/* 7.5) Phase 517 — Supplier Refund & Discount/Credit.
          Optional fields kept right after the main expense details so
          they are easy to spot but never get in the way of the common
          "add a regular expense" flow. The "Final Expense" preview at
          the bottom recomputes on every keystroke so the user can see
          the booked amount before saving. */}
      <details className="border border-zinc-200 bg-zinc-50/50 rounded-none" data-testid={tid('supplier-adjustments')}>
        <summary className="cursor-pointer px-3 py-2 text-xs font-semibold uppercase tracking-wider text-zinc-600 hover:bg-zinc-100">
          {t('supplierRefundCredit') || 'Supplier Refund / Credit'}
          <span className="ml-2 text-[10px] font-normal text-zinc-400 normal-case">
            {t('optional') || 'optional'}
          </span>
        </summary>
        <div className="px-3 py-3 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{t('discountCreditAmount') || 'Discount / Credit Amount'} (&euro;)</Label>
              <Input
                type="number" step="0.01" min="0"
                value={form.discount_amount ?? ''}
                onChange={(e) => setForm({ ...form, discount_amount: e.target.value === '' ? '' : (parseFloat(e.target.value) || 0) })}
                className="mt-1 rounded-none font-mono"
                placeholder="0.00"
                data-testid={tid('discount-amount')}
              />
            </div>
            <div>
              <Label className="text-xs">{t('refundAmount') || 'Refund Amount'} (&euro;)</Label>
              <Input
                type="number" step="0.01" min="0"
                value={form.refund_amount ?? ''}
                onChange={(e) => setForm({ ...form, refund_amount: e.target.value === '' ? '' : (parseFloat(e.target.value) || 0) })}
                className="mt-1 rounded-none font-mono"
                placeholder="0.00"
                data-testid={tid('refund-amount')}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{t('refundReference') || 'Refund Reference'}</Label>
              <Input
                value={form.refund_reference || ''}
                onChange={(e) => setForm({ ...form, refund_reference: e.target.value })}
                className="mt-1 rounded-none font-mono"
                placeholder="CR-2026-001"
                data-testid={tid('refund-reference')}
              />
            </div>
            <div>
              <Label className="text-xs">{t('refundDate') || 'Refund Date'}</Label>
              <DatePickerDMY value={form.refund_date || ''} onChange={v => setForm({ ...form, refund_date: v })} className="mt-1 font-mono" data-testid={tid('refund-date')} />
            </div>
          </div>
          <div>
            <Label className="text-xs">{t('refundNotes') || 'Refund Notes'}</Label>
            <Textarea
              value={form.refund_notes || ''}
              onChange={(e) => setForm({ ...form, refund_notes: e.target.value })}
              className="mt-1 rounded-none"
              rows={2}
              data-testid={tid('refund-notes')}
            />
          </div>

          {/* Live breakdown preview — same math as the backend helper. */}
          {(() => {
            const original = Number(form.amount || 0);
            const discount = Math.max(0, Math.min(original, Number(form.discount_amount || 0)));
            const refund = Math.max(0, Number(form.refund_amount || 0));
            const final = Math.max(0, original - discount);
            if (discount === 0 && refund === 0) return null;
            return (
              <div className="border border-zinc-300 bg-white px-3 py-2 mt-2 text-xs space-y-0.5 font-mono" data-testid={tid('supplier-breakdown')}>
                <div className="flex justify-between"><span className="text-zinc-500">{t('originalExpense') || 'Original Expense'}:</span><span>&euro;{original.toFixed(2)}</span></div>
                {discount > 0 && (
                  <div className="flex justify-between text-amber-700"><span>{t('supplierCredit') || 'Supplier Credit'}:</span><span>-&euro;{discount.toFixed(2)}</span></div>
                )}
                {refund > 0 && (
                  <div className="flex justify-between text-emerald-700"><span>{t('supplierRefund') || 'Supplier Refund'}:</span><span>+&euro;{refund.toFixed(2)}</span></div>
                )}
                <div className="flex justify-between font-bold border-t border-zinc-200 pt-1 mt-1"><span>{t('finalExpense') || 'Final Expense'}:</span><span>&euro;{final.toFixed(2)}</span></div>
              </div>
            );
          })()}
        </div>
      </details>

      {/* 8) Payment Source — Phase 309: Always paid. Only Cash + Bank.
          Phase 373.1: Reserve Fund is a separate flag (checkbox), not its
          own payment source — restoring the original two-control UX. */}
      {!isFullInsurance && (
        <div className="space-y-2">
          <div>
            <Label className="text-xs font-semibold uppercase text-zinc-500">{t('paymentSource')}</Label>
            <Select
              value={form.payment_source === 'reserve_fund' ? 'cash' : (form.payment_source === 'bank' ? 'bank' : 'cash')}
              onValueChange={v => setForm({ ...form, payment_source: v })}
            >
              <SelectTrigger className="rounded-none mt-1" data-testid={tid('payment-source')}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">{t('cash')} <span className={`ml-1 font-mono ${(sourceBalances.cash?.available || 0) < 0 ? 'text-red-500' : 'text-zinc-500'}`}>(&euro;{(sourceBalances.cash?.available || 0).toFixed(2)} {t('available')})</span></SelectItem>
                <SelectItem value="bank">{t('bank')} <span className={`ml-1 font-mono ${(sourceBalances.bank?.available || 0) < 0 ? 'text-red-500' : 'text-zinc-500'}`}>(&euro;{(sourceBalances.bank?.available || 0).toFixed(2)} {t('available')})</span></SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 cursor-pointer" data-testid={tid('reserve-fund-toggle')}>
            <input
              type="checkbox"
              checked={!!form.paid_from_reserve_fund}
              onChange={e => {
                const checked = e.target.checked;
                setForm(f => ({
                  ...f,
                  paid_from_reserve_fund: checked,
                  // Phase 708 — When ticking the checkbox, pre-fill the RF
                  // deduction amount with the full expense amount (single-
                  // source flow). When un-ticking, clear all structured
                  // fields so the audit trail stays clean.
                  reserve_fund_deduction_amount: checked
                    ? (f.reserve_fund_deduction_amount ?? f.amount ?? 0)
                    : null,
                  reserve_fund_reason: checked ? (f.reserve_fund_reason || '') : null,
                  reserve_fund_description: checked ? (f.reserve_fund_description || '') : null,
                  reserve_fund_reference: checked ? (f.reserve_fund_reference || '') : null,
                }));
              }}
              className="rounded-none w-4 h-4 accent-violet-600"
            />
            <span className="text-xs text-zinc-600">{t('paidFromReserveFund')}</span>
            <span className="text-[10px] font-mono text-zinc-400">(&euro;{(sourceBalances.reserve_fund?.available || 0).toFixed(2)} {t('available')})</span>
          </label>
        </div>
      )}

      {/* Phase 708 — Structured Reserve Fund Deduction panel.
          Appears immediately under the Payment Source row when "Paid from
          Reserve Fund" is checked. Captures Amount / Reason / Description /
          Reference so the Reserve Fund Statement keeps a complete audit
          trail and the user knows WHY money was taken from the reserve
          even 5+ years later. The backend validates that Reason is
          present and that the deduction amount does not exceed the
          expense amount. */}
      {!!form.paid_from_reserve_fund && (
        <div className="border border-violet-200 bg-violet-50/40 p-3 space-y-3" data-testid={tid('rf-deduction-panel')}>
          <div className="flex items-center justify-between">
            <Label className="text-xs font-semibold uppercase text-violet-700">
              {t('reserveFundDeduction') || 'Reserve Fund Deduction'}
            </Label>
            <span className="text-[10px] font-mono text-zinc-400">
              {t('expense') || 'Expense'}: €{Number(form.amount || 0).toFixed(2)}
            </span>
          </div>
          {/* Breakdown helper — only visible when deduction < expense amount */}
          {Number(form.reserve_fund_deduction_amount || 0) > 0 &&
           Number(form.reserve_fund_deduction_amount || 0) < Number(form.amount || 0) - 0.01 && (
            <div className="text-[11px] font-mono bg-white border border-violet-200 p-2 leading-relaxed">
              <div className="flex justify-between"><span>{t('expense') || 'Expense'}</span><span>€{Number(form.amount || 0).toFixed(2)}</span></div>
              <div className="flex justify-between text-zinc-600"><span>{t('coveredByAnnualBudget') || 'Covered by Annual Budget'}</span><span>€{(Number(form.amount || 0) - Number(form.reserve_fund_deduction_amount || 0)).toFixed(2)}</span></div>
              <div className="flex justify-between text-violet-700 font-semibold"><span>{t('deductFromReserveFund') || 'Deduct from Reserve Fund'}</span><span>€{Number(form.reserve_fund_deduction_amount || 0).toFixed(2)}</span></div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] font-semibold uppercase text-zinc-600">
                {t('amountDeducted') || 'Amount Deducted'} (€)
              </Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.reserve_fund_deduction_amount ?? ''}
                onChange={e => setForm({ ...form, reserve_fund_deduction_amount: e.target.value === '' ? null : parseFloat(e.target.value) })}
                className="rounded-none mt-1 font-mono"
                data-testid={tid('rf-amount')}
              />
            </div>
            <div>
              <Label className="text-[11px] font-semibold uppercase text-zinc-600">
                {t('reason') || 'Reason'} <span className="text-red-500">*</span>
              </Label>
              <Select
                value={form.reserve_fund_reason || ''}
                onValueChange={v => setForm({ ...form, reserve_fund_reason: v })}
              >
                <SelectTrigger className="rounded-none mt-1" data-testid={tid('rf-reason')}>
                  <SelectValue placeholder={t('selectReason') || 'Select reason…'} />
                </SelectTrigger>
                <SelectContent>
                  {rfReasons.map(r => (
                    <SelectItem key={r} value={r}>{t(`rfReason_${r.replace(/\s+/g, '')}`) || r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-[11px] font-semibold uppercase text-zinc-600">
              {t('description') || 'Description'}
            </Label>
            <Textarea
              value={form.reserve_fund_description || ''}
              onChange={e => setForm({ ...form, reserve_fund_description: e.target.value })}
              placeholder={t('rfDescriptionPlaceholder') || 'e.g. Electricity bill exceeded annual budget by €200'}
              className="rounded-none mt-1 text-sm"
              rows={2}
              data-testid={tid('rf-description')}
            />
          </div>
          <div>
            <Label className="text-[11px] font-semibold uppercase text-zinc-600">
              {t('referenceOptional') || 'Reference (optional)'}
            </Label>
            <Input
              value={form.reserve_fund_reference || ''}
              onChange={e => setForm({ ...form, reserve_fund_reference: e.target.value })}
              placeholder={t('rfReferencePlaceholder') || 'e.g. EAC Invoice 24/06/2025'}
              className="rounded-none mt-1 font-mono text-sm"
              data-testid={tid('rf-reference')}
            />
          </div>
        </div>
      )}


      {/* 9) Attachments — Phase 308 */}
      <div className="border-t border-zinc-100 pt-4">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs font-semibold uppercase text-zinc-500">{t('attachments') || 'Attachments'}</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-none h-7 text-xs"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            data-testid={tid('attach-files-btn')}
          >
            <Paperclip size={14} className="mr-1" />{t('attachFiles') || 'Attach Files'}
          </Button>
        </div>
        <p className="text-[10px] text-zinc-400 mb-2">PDF, JPG, PNG, HEIC, Excel, Word, CSV ({ALLOWED_EXT.join(', ').toUpperCase()})</p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => {
            if (onPickFiles && e.target.files && e.target.files.length > 0) onPickFiles(e.target.files);
            // Reset so re-selecting the same file works
            e.target.value = '';
          }}
          data-testid={tid('attach-file-input')}
        />

        {/* Persisted attachments (Edit mode mostly) */}
        {attachments && attachments.length > 0 && (
          <ul className="space-y-1 mb-2">
            {attachments.map((a) => (
              <li
                key={a.attachment_id}
                className="flex items-center gap-2 px-2 py-1.5 border border-zinc-200 bg-white"
                data-testid={tid(`attachment-saved-${a.attachment_id}`)}
              >
                <IconForFile name={a.original_name} />
                <span className="text-xs text-zinc-700 truncate flex-1" title={a.original_name}>{a.original_name}</span>
                <span className="text-[10px] text-zinc-400 font-mono">{formatBytes(a.size)}</span>
                <button
                  type="button"
                  className="text-zinc-500 hover:text-zinc-900"
                  onClick={() => onDownloadAttachment && onDownloadAttachment(a.attachment_id, a.original_name)}
                  title={t('download') || 'Download'}
                  data-testid={tid(`attachment-download-${a.attachment_id}`)}
                >
                  <DownloadSimple size={14} />
                </button>
                <button
                  type="button"
                  className="text-zinc-400 hover:text-red-600"
                  onClick={() => onRemoveAttachment && onRemoveAttachment(a.attachment_id)}
                  title={t('delete') || 'Delete'}
                  data-testid={tid(`attachment-remove-${a.attachment_id}`)}
                >
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Queued (pending) files — waiting for Save */}
        {pendingFiles && pendingFiles.length > 0 && (
          <ul className="space-y-1">
            {pendingFiles.map((f, i) => (
              <li
                key={`${f.name}-${i}`}
                className="flex items-center gap-2 px-2 py-1.5 border border-dashed border-zinc-300 bg-zinc-50"
                data-testid={tid(`attachment-pending-${i}`)}
              >
                <IconForFile name={f.name} />
                <span className="text-xs text-zinc-700 truncate flex-1" title={f.name}>{f.name}</span>
                <span className="text-[10px] text-zinc-400 font-mono">{formatBytes(f.size)}</span>
                <span className="text-[10px] uppercase font-semibold text-amber-600">{t('pending') || 'Pending'}</span>
                <button
                  type="button"
                  className="text-zinc-400 hover:text-red-600"
                  onClick={() => onRemovePending && onRemovePending(i)}
                  data-testid={tid(`attachment-pending-remove-${i}`)}
                >
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}

        {(!attachments || attachments.length === 0) && (!pendingFiles || pendingFiles.length === 0) && (
          <p className="text-[10px] text-zinc-400 italic">{t('noAttachments') || 'No attachments yet.'}</p>
        )}
      </div>
    </div>
  );
}
